import { type PremiereChapter, type PremiereMarker } from './premiereSharedTypes';

/**
 * Events sent from the Air WebView app to the Premiere UXP host
 */
const _AIR_TO_PREMIERE_EVENT = {
  /** Create chapter range markers from Air video chapters (clip markers when reachable, sequence markers otherwise) */
  CreateChapterMarkers: 'air-to-premiere:create-chapter-markers',
  /** Create markers from Air discussions (clip markers when reachable, sequence markers otherwise) */
  CreateMarkers: 'air-to-premiere:create-markers',
  /** Create a project bin for subsequent imports */
  CreateProjectBin: 'air-to-premiere:create-project-bin',
  /** Request info about the active sequence */
  GetActiveSequence: 'air-to-premiere:get-active-sequence',
  /** Import a video asset into the Premiere project */
  ImportAsset: 'air-to-premiere:import-asset',
  /** Download multiple assets then import them in a single Premiere importFiles call */
  ImportAssetBatch: 'air-to-premiere:import-asset-batch',
  /** Import Air transcript segments into the active clip's Premiere Text panel */
  ImportTranscript: 'air-to-premiere:import-transcript',
  /** Enumerate available .epr export presets from Adobe's preset directories */
  ListExportPresets: 'air-to-premiere:list-export-presets',
  /** Open a URL in the system browser (UXP cannot navigate WebView to external OAuth domains) */
  OpenUrl: 'air-to-premiere:open-url',
  /** Queue the active sequence for export via Adobe Media Encoder */
  QueueExport: 'air-to-premiere:queue-export',
  /** WebView signals to the host that it has finished loading */
  Ready: 'air-to-premiere:ready',
  /** Scrub the active sequence's playhead to a specific timeline time */
  RevealInTimeline: 'air-to-premiere:reveal-in-timeline',
  /** Upload a rendered file to Air (called after export completes) */
  UploadToAir: 'air-to-premiere:upload-to-air',
} as const;

/** @public */
export const AirToPremiereEvent = _AIR_TO_PREMIERE_EVENT;

/** @public */
export type AirToPremiereEvent = (typeof AirToPremiereEvent)[keyof typeof AirToPremiereEvent];

/**
 * Message: Create (or reuse) a project bin for batch imports
 * @public
 */
export type AirToPremiereCreateProjectBin = {
  type: typeof AirToPremiereEvent.CreateProjectBin;
  data: {
    /** Stable opaque key used as ImportAsset `targetBin` */
    binKey: string;
    /** Display name for the bin in Premiere's project panel */
    name: string;
  };
};

/**
 * Message: Import a video asset into the Premiere project
 * @public
 */
export type AirToPremiereImportAsset = {
  type: typeof AirToPremiereEvent.ImportAsset;
  data: {
    /** Download URL for the asset */
    url: string;
    /** Original filename */
    fileName: string;
    /** Opaque host registry key for a Premiere project bin (see CreateProjectBin) */
    targetBin?: string;
  };
};

/**
 * Message: Import multiple video assets in one Premiere importFiles call
 * @public
 */
export type AirToPremiereImportAssetBatch = {
  type: typeof AirToPremiereEvent.ImportAssetBatch;
  data: {
    /** Correlates with {@link PremiereToAirImportBatchComplete} */
    batchId: string;
    items: Array<{
      /** Download URL for the asset */
      url: string;
      /** Original filename */
      fileName: string;
      /** Per-item bin override; falls back to batch `targetBin` when omitted */
      targetBin?: string;
    }>;
    /** Opaque host registry key for a Premiere project bin (see CreateProjectBin) */
    targetBin?: string;
  };
};

/**
 * Message: Create markers from Air discussions. The host attaches them to
 * the source ClipProjectItem (so they travel with the clip across timeline
 * placements) when the matched track item exposes one, otherwise falls
 * back to sequence markers with a timeline offset.
 * @public
 */
export type AirToPremiereCreateMarkers = {
  type: typeof AirToPremiereEvent.CreateMarkers;
  data: {
    markers: PremiereMarker[];
    /** Asset name to match against clips in the sequence for correct timeline positioning */
    assetName?: string;
  };
};

/**
 * Message: Create chapter range markers from Air video chapters. Same
 * host placement strategy as `CreateMarkers` — clip markers on the source
 * ClipProjectItem when reachable, sequence markers otherwise.
 * @public
 */
export type AirToPremiereCreateChapterMarkers = {
  type: typeof AirToPremiereEvent.CreateChapterMarkers;
  data: {
    chapters: PremiereChapter[];
    /** Asset name to match against clips in the sequence for correct timeline positioning */
    assetName?: string;
  };
};

/**
 * Message: Queue the active sequence for export
 * @public
 */
export type AirToPremiereQueueExport = {
  type: typeof AirToPremiereEvent.QueueExport;
  data: {
    /**
     * Native filesystem path to a `.epr` preset file. Required — Adobe's
     * `EncoderManager.exportSequence` rejects null/empty paths with
     * "Illegal Parameter type".
     */
    presetPath: string;
  };
};

/**
 * Message: Enumerate available .epr export presets from Adobe's preset
 * directories. The host scans the bundled Premiere preset folder and the
 * user's AME preset folders, then responds with {@link PremiereToAirExportPresets}.
 * @public
 */
export type AirToPremiereListExportPresets = {
  type: typeof AirToPremiereEvent.ListExportPresets;
};

/**
 * Message: Upload a rendered file from the local filesystem to Air.
 * The UXP host reads the file, gets a signed upload URL from Air's API,
 * and performs the upload — the WebView can't access local files.
 * @public
 */
export type AirToPremiereUploadToAir = {
  type: typeof AirToPremiereEvent.UploadToAir;
  data: {
    /** Local file path of the rendered output (from ExportComplete) */
    filePath: string;
    /** Filename for the uploaded asset */
    fileName: string;
    /** Workspace to upload into */
    workspaceId: string;
    /** Board to add the asset to (optional) */
    boardId?: string;
    /** Existing asset ID to create a new version (optional) */
    assetId?: string;
    /** API endpoint base URL for the upload requests */
    apiEndpoint: string;
    /** JWT auth token for API requests */
    authToken: string;
  };
};

/**
 * Message: Request info about the active sequence
 * @public
 */
export type AirToPremiereGetActiveSequence = {
  type: typeof AirToPremiereEvent.GetActiveSequence;
};

/**
 * Message: Scrub the active sequence's playhead to a specific timeline time.
 * Used by "jump to comment" / "jump to chapter" from the Air WebView — the
 * host resolves the time against the linked clip's offset so the seek lands
 * on the same frame the viewer sees in Air.
 * @public
 */
export type AirToPremiereRevealInTimeline = {
  type: typeof AirToPremiereEvent.RevealInTimeline;
  data: {
    /** Target time in seconds from the start of the asset in Air */
    timeSeconds: number;
    /** Asset name to resolve the clip offset against, same as marker events */
    assetName?: string;
  };
};

/**
 * Message: Open a URL in the system browser via UXP shell.openExternal.
 * Used for OAuth sign-in — UXP WebView cannot navigate to external auth
 * domains (Google, Apple) that aren't in the webview allowlist.
 * @public
 */
export type AirToPremiereOpenUrl = {
  type: typeof AirToPremiereEvent.OpenUrl;
  data: { url: string };
};

/**
 * Message: WebView signals to the host that it has finished loading.
 * The host may use this to dismiss a splash screen or start sending initial state.
 * @public
 */
export type AirToPremiereReady = {
  type: typeof AirToPremiereEvent.Ready;
};

/**
 * Message: Import Air transcript segments into the active clip's Premiere Text panel.
 * The host calls Transcript.importFromJSON + createImportTextSegmentsAction on the
 * ClipProjectItem. assetName is used to resolve which clip to attach to, same
 * strategy as marker events.
 * @public
 */
export type AirToPremiereImportTranscript = {
  type: typeof AirToPremiereEvent.ImportTranscript;
  data: {
    /** SRT file content fetched from clip.assets.transcriptSrt */
    srtText: string;
    /** Filename for the imported caption item (e.g. "My Video.srt") */
    fileName: string;
  };
};

/**
 * Union type of all messages from Air WebView to Premiere UXP host
 * @public
 */
export type AirToPremiereMessage =
  | AirToPremiereImportAsset
  | AirToPremiereImportAssetBatch
  | AirToPremiereCreateMarkers
  | AirToPremiereCreateChapterMarkers
  | AirToPremiereCreateProjectBin
  | AirToPremiereQueueExport
  | AirToPremiereUploadToAir
  | AirToPremiereGetActiveSequence
  | AirToPremiereListExportPresets
  | AirToPremiereOpenUrl
  | AirToPremiereRevealInTimeline
  | AirToPremiereReady
  | AirToPremiereImportTranscript;
